import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { type ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import {
  ManagerProgressionTaskState,
  TaskLineupType,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Refresh } from '@sorare/core/src/atoms/icons/Refresh';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { ArrowDownShape as ArrowDownShapeSvg } from '@sorare/core/src/atoms/ui/ArrowDownShape/index.tsx';
import { JonumKycPromptDialog } from '@sorare/core/src/components/jonum/JonumVerificationDialog/JonumKycPromptDialog';
import {
  getKycClaimState,
  useJonumVerificationGuard,
} from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { ANY_SPORT_PLAY_BOARDS_PRO } from '@sorare/core/src/constants/__generated__/routes.ts';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { isType } from '@sorare/core/src/lib/gql/index.ts';
import { playablePositionsBySport } from '@sorare/core/src/lib/players.ts';

import { Lifes } from '#src/components/boards/Lifes/index.tsx';
import { SignFirstPlayersDialog } from '#src/components/onboarding/SignFirstPlayersDialog/index.tsx';
import { SeriesBoardOnboarding } from '#src/components/series/SeriesBoard/SeriesBoardOnboarding/index.tsx';
import { TrainingSetDialog } from '#src/components/series/TrainingSetDialog/index.tsx';

import { ActionRequiredCta } from './ActionRequiredCta/index.tsx';
import { CheckmarkTile } from './CheckmarkTile/index.tsx';
import { ClaimableTile } from './ClaimableTile/index.tsx';
import { FailedTile } from './FailedTile/index.tsx';
import { LockedTile } from './LockedTile/index.tsx';
import { PendingVerificationCta } from './PendingVerificationCta/index.tsx';
import { PrepareLineupTooltipContent } from './PrepareLineupTooltipContent/index.tsx';
import { ReadyAndSelectedTile } from './ReadyAndSelectedTile/index.tsx';
import { ReadyIcon, ReadyIconWrapper } from './ReadyIcon/index.tsx';
import { ReadyTile } from './ReadyTile/index.tsx';
import { TaskTooltipContent } from './TaskTooltipContent/index.tsx';
import type { TooltipContent_anyRewardConfigInterface } from './TooltipContent/__generated__/index.graphql.ts';
import type {
  HotStreakTileTopRecommendationsQuery,
  HotStreakTileTopRecommendationsQueryVariables,
  HotStreakTile_thresholdPickerTask,
} from './__generated__/index.graphql.ts';
import lockedTile from './assets/lockedTile.webp';
import prepareIcon from './assets/prepareIcon.webp';
import {
  CroppedPlayerImage,
  Dot,
  greenOverlay,
  LiveChip,
  orangeOverlay,
  redOverlay,
  TileWrapper,
} from './ui.tsx';

const TOP_RECOMMENDATIONS_QUERY = gql`
  query HotStreakTileTopRecommendationsQuery(
    $taskId: ID!
    $boardId: String!
    $sport: Sport!
    $positions: [Position!]
  ) {
    currentUser {
      slug
      board(id: $boardId) {
        id
        ...SignFirstPlayersDialog_careerBoard
      }
      task(id: $taskId) {
        id
        ... on ThresholdPickerTask {
          id
          topRecommendations(
            recommendationEngine: POPULAR_OR_HOT_STREAK
            sport: $sport
            filters: { positions: $positions }
          ) {
            id
            anyCardWithLivePrimaryOffer {
              slug
            }
          }
        }
      }
    }
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        ...TrainingSetDialog_cardSet
      }
      nextSet(sport: $sport) {
        slug
        ...TrainingSetDialog_cardSet
      }
    }
  }
  ${SignFirstPlayersDialog.fragments.careerBoard}
  ${TrainingSetDialog.fragments.cardSet}
` as TypedDocumentNode<
  HotStreakTileTopRecommendationsQuery,
  HotStreakTileTopRecommendationsQueryVariables
>;

const StyledButtonBase = styled(ButtonBase)`
  z-index: 1;
`;

const ActionRequiredTile = styled.div`
  position: relative;
  display: inline-flex;
`;

const ActionRequiredCtaWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
`;

const UnderlineLink = styled(Link)`
  text-decoration: underline;
`;

type Props = {
  task: HotStreakTile_thresholdPickerTask;
  showOnboarding?: boolean;
  onClaimTask: (id: string) => void;
  onOpenHotStreaksTutorialStory?: () => void;
  showTargetValue?: boolean;
  setShowTargetValue?: (showTargetValue: boolean) => void;
  lineupsPath: string;
  composePath: string;
  oldThreshold?: number | null;
  isReplay?: boolean;
  originalTarget?: number | null;
  originalRewardConfig?: TooltipContent_anyRewardConfigInterface | null;
  boardId?: string;
  fromPro?: boolean;
};
export const HotStreakTile = ({
  task,
  showOnboarding = false,
  onClaimTask,
  onOpenHotStreaksTutorialStory,
  showTargetValue = false,
  setShowTargetValue,
  lineupsPath,
  composePath,
  oldThreshold = null,
  isReplay = false,
  originalTarget = null,
  originalRewardConfig = null,
  boardId,
  fromPro = false,
}: Props) => {
  const navigate = useNavigate();
  const { lifecycle } = useLifecycle();
  const { generateSportContextPath, sport } = useSportContext();
  const { kycRequired, mangopayKycUnderReview } = useJonumVerificationGuard();
  const [openTrainingSetDialog, setOpenTrainingSetDialog] = useState(false);
  const [openKycPromptDialog, setOpenKycPromptDialog] = useState(false);
  const [signFirstPlayersRequested, setSignFirstPlayersRequested] =
    useState(false);
  const sawSignFirstPlayersDialog =
    !!lifecycle?.[LIFECYCLE.sawSignFirstPlayersDialog];
  const { data: topRecommendationsData, loading: loadingTopRecommendations } =
    useQuery(TOP_RECOMMENDATIONS_QUERY, {
      variables: {
        taskId: idFromObject(task.id),
        boardId: boardId ?? '',
        sport,
        positions: [...playablePositionsBySport(sport)],
      },
      skip:
        !fromPro ||
        !boardId ||
        task.aasmState !== ManagerProgressionTaskState.READY ||
        sawSignFirstPlayersDialog,
    });
  const board = topRecommendationsData?.currentUser?.board;
  const careerBoard = board && isType(board, 'CareerBoard') ? board : null;
  const taskWithRecommendations = topRecommendationsData?.currentUser?.task;
  const hasTopRecommendations =
    !!taskWithRecommendations &&
    isType(taskWithRecommendations, 'ThresholdPickerTask') &&
    taskWithRecommendations.topRecommendations.filter(
      reco => !!reco.anyCardWithLivePrimaryOffer
    ).length > 0;
  const {
    id,
    aasmState,
    rewardConfigs,
    averageMode,
    displayedPlayer,
    taskLineups,
    taskPreparedLineups,
    parentTask,
    canPrepareLineups,
  } = task;

  const kycState = getKycClaimState(
    { kycRequired, mangopayKycUnderReview },
    rewardConfigs
  );
  const kycActionRequired = kycState === 'actionRequired';
  const kycPendingVerification = kycState === 'pendingVerification';

  const tooltipContent = (
    <TaskTooltipContent
      task={task}
      showTargetValue={showTargetValue}
      oldThreshold={oldThreshold}
      originalTarget={originalTarget}
      originalRewardConfig={originalRewardConfig}
    />
  );

  const hasSharedPool = rewardConfigs.some(
    rc => isType(rc, 'MonetaryRewardConfig') && rc.sharedPool
  );

  const isParentTrackCancelled =
    isType(parentTask, 'TasksTrack') &&
    parentTask.aasmState === ManagerProgressionTaskState.CANCELED;

  const hasSubmittedLineups = taskLineups.length > 0;
  const hasFailedAllSubmittedLineups =
    hasSubmittedLineups &&
    taskLineups.every(lineup => lineup.aasmState === TaskLineupType.FAILED);

  const hasFailedReplay =
    averageMode &&
    aasmState === ManagerProgressionTaskState.READY &&
    hasFailedAllSubmittedLineups;

  const to = averageMode ? composePath : lineupsPath;

  if (
    hasFailedReplay ||
    task.aasmState === ManagerProgressionTaskState.CANCELED
  ) {
    return (
      <StyledButtonBase to={to}>
        <TileWrapper>
          <FailedTile />
          <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
            <Vertical center>
              <Refresh />
              <BrandS>
                <FormattedMessage defaultMessage="Miss" />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.READY) {
    if (hasSubmittedLineups) {
      if (hasFailedAllSubmittedLineups) {
        return (
          <StyledButtonBase to={lineupsPath}>
            <TileWrapper>
              <FailedTile />
              <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
                <CroppedPlayerImage
                  style={{
                    background: `${redOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                  }}
                />
                <LiveChip color="black">
                  <Lifes task={task} size={1} />
                </LiveChip>
              </ArrowDownShapeSvg>
            </TileWrapper>
          </StyledButtonBase>
        );
      }

      const hasLiveGames = task.taskLineups.some(lineup => lineup.hasLiveGames);
      return (
        <StyledButtonBase to={lineupsPath}>
          <TileWrapper>
            <ReadyAndSelectedTile />
            <ArrowDownShapeSvg
              animatedOnHover
              colors={{ start: '#FEF314', end: '#FF4E22' }}
            >
              <CroppedPlayerImage
                style={{
                  background: `${orangeOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                }}
              />
              {!!hasLiveGames && (
                <LiveChip color="black">
                  <Dot />
                  <BrandXXS>
                    <FormattedMessage defaultMessage="Live" />
                  </BrandXXS>
                </LiveChip>
              )}
            </ArrowDownShapeSvg>
          </TileWrapper>
        </StyledButtonBase>
      );
    }

    const onClick = () => {
      if (fromPro && !hasTopRecommendations && !sawSignFirstPlayersDialog) {
        setSignFirstPlayersRequested(true);
        return;
      }
      if (averageMode) {
        setOpenTrainingSetDialog(true);
        return;
      }
      if (
        !lifecycle?.sawSeriesHotStreakOnboarding &&
        onOpenHotStreaksTutorialStory
      ) {
        onOpenHotStreaksTutorialStory?.();
        return;
      }
      navigate(to);
    };

    if (fromPro && !sawSignFirstPlayersDialog && loadingTopRecommendations) {
      return null;
    }

    return (
      <>
        <OnboardingHighlightWrapper
          highlightName="SeriesBoardReplayButton"
          wrap={!!showOnboarding}
        >
          <TileWrapper>
            <ButtonBase
              onClick={onClick}
              className="flex h-full w-full items-center justify-center"
            >
              <ReadyTile />
            </ButtonBase>
            <ReadyIconWrapper className="animated">
              <Tooltip
                open
                placement="top"
                onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
                showInPortal={false}
                title={tooltipContent}
              >
                <ButtonBase onClick={onClick} className="flex">
                  <ReadyIcon className={classNames({ averageMode })} />
                </ButtonBase>
              </Tooltip>
            </ReadyIconWrapper>
          </TileWrapper>
        </OnboardingHighlightWrapper>
        <SeriesBoardOnboarding isReplay={!!averageMode} onClose={onClick} />
        {averageMode ? (
          <TrainingSetDialog
            task={task}
            open={openTrainingSetDialog}
            onClose={() => setOpenTrainingSetDialog(false)}
            to={to}
            cardSet={
              topRecommendationsData?.cardSet?.currentSet ??
              topRecommendationsData?.cardSet?.nextSet
            }
          />
        ) : null}
        {fromPro ? (
          <OneTimeDialog
            dialogId={LIFECYCLE.sawSignFirstPlayersDialog}
            show={signFirstPlayersRequested}
          >
            {({ open, onClose }) => (
              <SignFirstPlayersDialog
                open={open}
                onClose={onClose}
                careerBoard={careerBoard}
              />
            )}
          </OneTimeDialog>
        ) : null}
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.COMPLETED) {
    if (hasSharedPool && isParentTrackCancelled) {
      return (
        <Tooltip
          open
          placement="bottom"
          showInPortal={false}
          title={
            <FormattedMessage
              defaultMessage="Board completed — you're in the winners list! <link>Go to play</link> to take on another one."
              values={{
                link: (chunks: ReactNode) => (
                  <UnderlineLink
                    to={generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO)}
                  >
                    {chunks}
                  </UnderlineLink>
                ),
              }}
            />
          }
        >
          <div>
            <TileWrapper>
              <CheckmarkTile />
              <ArrowDownShapeSvg colors={{ start: '#00F34D', end: '#00D5FF' }}>
                <CroppedPlayerImage
                  style={{
                    background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                  }}
                />
              </ArrowDownShapeSvg>
            </TileWrapper>
          </div>
        </Tooltip>
      );
    }

    const getArrowColors = () => {
      if (kycPendingVerification) {
        return { start: '#000000', end: '#B5B5B5' };
      }
      if (kycActionRequired) {
        return { start: '#7A5A00', end: '#FFD914' };
      }
      return undefined;
    };

    const claimableTile = (
      <StyledButtonBase
        {...(averageMode
          ? { onClick: () => onClaimTask(id) }
          : { to: lineupsPath })}
      >
        <TileWrapper>
          <ClaimableTile
            gold={kycActionRequired}
            pending={kycPendingVerification}
          />
          <ArrowDownShapeSvg floating colors={getArrowColors()}>
            <Vertical gap={0} center>
              <RewardConfigImage anyRewardConfig={rewardConfigs[0]} />
              <BrandS>
                <RewardConfigText anyRewardConfig={rewardConfigs[0]} />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );

    if (kycPendingVerification) {
      return (
        <ActionRequiredTile>
          {claimableTile}
          <ActionRequiredCtaWrapper>
            <PendingVerificationCta />
          </ActionRequiredCtaWrapper>
        </ActionRequiredTile>
      );
    }

    if (!kycActionRequired) {
      return claimableTile;
    }

    return (
      <>
        <ActionRequiredTile>
          {claimableTile}
          <ActionRequiredCtaWrapper>
            <ActionRequiredCta onClick={() => setOpenKycPromptDialog(true)} />
          </ActionRequiredCtaWrapper>
        </ActionRequiredTile>
        <JonumKycPromptDialog
          open={openKycPromptDialog}
          onDone={() => setOpenKycPromptDialog(false)}
        />
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    return (
      <StyledButtonBase
        to={averageMode ? undefined : lineupsPath}
        as={averageMode ? 'div' : undefined}
      >
        <TileWrapper>
          <CheckmarkTile />
          <ArrowDownShapeSvg colors={{ start: '#00F34D', end: '#00D5FF' }}>
            <CroppedPlayerImage
              style={{
                background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
              }}
            />
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.ASSIGNED) {
    if (canPrepareLineups) {
      return (
        <Tooltip
          open={!isReplay}
          placement="bottom"
          showInPortal={false}
          title={
            <PrepareLineupTooltipContent count={taskPreparedLineups.length} />
          }
        >
          <TileWrapper>
            <StyledButtonBase
              onClick={() => navigate(lineupsPath)}
              className="flex h-full w-full items-center justify-center"
            >
              <LockedTile />
            </StyledButtonBase>
            <div className="bottom-1 absolute z-2">
              <Tooltip
                open={!isReplay}
                placement="top"
                showInPortal={false}
                onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
                title={tooltipContent}
              >
                <ButtonBase
                  onClick={() => navigate(lineupsPath)}
                  className="flex"
                >
                  <img
                    src={prepareIcon}
                    alt="prepare icon"
                    width={48}
                    height={48}
                  />
                </ButtonBase>
              </Tooltip>
            </div>
          </TileWrapper>
        </Tooltip>
      );
    }
    return (
      <TileWrapper>
        <Tooltip
          open={!isReplay}
          placement="top"
          showInPortal={false}
          onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
          title={tooltipContent}
        >
          <img src={lockedTile} alt="locked tile" width={30} />
        </Tooltip>
      </TileWrapper>
    );
  }

  return null;
};

HotStreakTile.fragments = {
  thresholdPickerTask: gql`
    fragment HotStreakTile_thresholdPickerTask on ThresholdPickerTask {
      id
      aasmState
      averageMode
      canPrepareLineups
      rewardConfigs {
        id
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
      }
      displayedPlayer {
        slug
        squaredPictureUrl
      }
      taskLineups {
        id
        aasmState
        hasLiveGames
      }
      taskPreparedLineups {
        id
      }
      parentTask {
        id
        ... on TasksTrack {
          id
          aasmState
        }
      }
      ...TrainingSetDialog_taskInterface
      ...Lifes_thresholdPickerTask
      ...TaskTooltipContent_thresholdPickerTask
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${TrainingSetDialog.fragments.taskInterface}
    ${Lifes.fragments.thresholdPickerTask}
    ${TaskTooltipContent.fragments.thresholdPickerTask}
  ` as TypedDocumentNode<HotStreakTile_thresholdPickerTask>,
};
